import * as AriaKit from '@ariakit/react'
import classnames from 'classnames'
import * as React from 'react'

import { isIos } from '../../../app/shared/isMobile'
import { TooltipSettingsPropType } from './hooks'
import { TooltipArrow } from './tooltip-arrow'
import { TooltipBox, TooltipBoxProps } from './tooltip-box'

export interface TooltipProps extends TooltipBoxProps, Pick<AriaKit.TooltipProps, 'gutter' | 'hideOnHoverOutside'> {
  /**
   * The returned object from `useTooltipState`
   */
  state: TooltipSettingsPropType
}

const isIosBrowser = isIos()

export function Tooltip({
  state,
  children,
  gutter,
  hideOnHoverOutside,
  color = 'none',
  ...props
}: React.PropsWithChildren<TooltipProps>) {
  const placement = state.useState('currentPlacement')

  return (
    <AriaKit.Tooltip
      store={state}
      focusable={false}
      // On iOS depending on composition TooltipTrigger doesn't work properly if Tooltip isn't always rendered
      // and causes bug on interactive elements with tooltip like selects, buttons, etc. when on first click
      // only tooltip appears but select, button or etc. doesn't receive event so it won't open, also on first click
      // in iOS safari onClick event isn't fired no TooltipTrigger, just onPointerDown, and thus further breaking logic
      // because our custom callback which are placed in onClick aren't invoked, after debugging we think that this
      // is bug caused by combination of ariakit behaviour when <Tooltip> isn't always rendered and way of events
      // firing by iOS Safari
      unmountOnHide={!isIosBrowser}
      wrapperProps={{
        className: 'pointer-events-none',
      }}
      className={classnames(
        'pointer-events-none z-dialog opacity-0 outline-none transition duration-[250ms] will-change-transform data-[enter]:translate-x-0 data-[enter]:translate-y-0 data-[enter]:opacity-100',
        {
          'motion-safe:translate-y-1': placement.startsWith('top'),
          'motion-safe:-translate-x-1': placement.startsWith('right'),
          'motion-safe:-translate-y-1': placement.startsWith('bottom'),
          'motion-safe:translate-x-1': placement.startsWith('left'),
        }
      )}
      hideOnHoverOutside={hideOnHoverOutside}
      gutter={gutter}
    >
      <TooltipBox {...props} color={color} data-placement={placement}>
        <AriaKit.TooltipArrow store={state} size={11}>
          <TooltipArrow placement={placement} color={color} />
        </AriaKit.TooltipArrow>
        {children}
      </TooltipBox>
    </AriaKit.Tooltip>
  )
}
